
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { X, ClipboardList, Edit2, Save, Trash2, Paperclip, Upload, Loader2, File, Download, MessageSquare, Clock, CheckCircle, Send, AlertTriangle, Building, Calendar, Phone, UserCheck, AtSign, Check, MessageCircle, ArrowRightCircle, Plus, RotateCcw, User as UserIcon, Tag, Radio, Briefcase, Globe } from 'lucide-react';
import { Attendance, AttendanceStatus, User, Contact, TaskComment, Attachment, UserRole } from '../types';
import { supabase } from '../supabase';
import SearchableSelect from './SearchableSelect';
import MentionInput from './MentionInput';
import FormattedText from './FormattedText';
import { useDraftState } from '../hooks/useDraftState';
import { extractMentionedUserIds } from '../utils/mentions';

interface AttendanceDetailModalProps {
  attendance: Attendance;
  users: User[];
  contacts: Contact[];
  onClose: () => void;
  onUpdateAttendance: (id: string, updates: Partial<Attendance>) => void;
  onDeleteAttendance: (id: string) => Promise<boolean>;
  currentUser: User;
}

// Helper para parse seguro
const parseAttachment = (att: any): Attachment => {
  if (typeof att === 'string') {
    try {
      return JSON.parse(att);
    } catch (e) {
      console.error("Erro ao fazer parse do anexo:", att);
      return { id: 'error', name: 'Erro no arquivo', url: '#', timestamp: new Date().toISOString() };
    }
  }
  return att;
};

const AttendanceDetailModal: React.FC<AttendanceDetailModalProps> = ({ attendance, users, contacts, onClose, onUpdateAttendance, onDeleteAttendance, currentUser }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [newComment, setNewComment] = useState('');

  const [editedFields, setEditedFields, clearDraft] = useDraftState(`edit_att_${attendance.id}`, {
    demand: attendance.demand || '', 
    summary: attendance.summary || '',
    urgency: attendance.urgency || 'Média', 
    status: attendance.status || AttendanceStatus.PENDING,
    responsible_id: attendance.responsible_id || '', 
    contactid: attendance.contactid || '', 
    channel: attendance.channel || 'WhatsApp',
    origin_details: attendance.origin_details || '',
    attachments: (attendance.attachments || []).map(parseAttachment)
  });

  // Sincroniza o estado local com os dados mais recentes do atendimento quando não estiver editando
  // Isso garante que ao clicar em "Editar", os dados estejam atualizados com o banco
  useEffect(() => {
    if (!isEditing) {
      setEditedFields(prev => ({
        ...prev,
        demand: attendance.demand || '',
        summary: attendance.summary || '',
        urgency: attendance.urgency || 'Média',
        status: attendance.status || AttendanceStatus.PENDING,
        responsible_id: attendance.responsible_id || '',
        contactid: attendance.contactid || '',
        channel: attendance.channel || 'WhatsApp',
        attachments: (attendance.attachments || []).map(parseAttachment)
      }));
    }
  }, [attendance, isEditing, setEditedFields]);

  const isLeadership = currentUser.role === UserRole.LEADERSHIP;
  const isCreator = attendance.created_by === currentUser.id || attendance.origin_details === currentUser.id;
  const canEdit = !isLeadership || (currentUser.permissions.canEditOwnContent && isCreator);

  const contact = useMemo(() => contacts.find(c => c.id === attendance.contactid), [attendance, contacts]);
  const responsible = useMemo(() => users.find(u => u.id === attendance.responsible_id), [attendance, users]);
  
  const handleSave = async () => {
    if(uploading) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      await onUpdateAttendance(attendance.id, {
        demand: editedFields.demand, 
        summary: editedFields.summary,
        urgency: editedFields.urgency, 
        status: editedFields.status,
        responsible_id: editedFields.responsible_id || null, 
        contactid: editedFields.contactid, 
        channel: editedFields.channel,
        origin_details: editedFields.origin_details || null,
        attachments: editedFields.attachments 
      });
      clearDraft();
      setIsEditing(false);
    } catch (err: any) { 
      console.error("Erro no save:", err);
      setErrorMsg("Erro ao salvar as alterações."); 
    } finally { 
      setSaving(false); 
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    const comment: TaskComment = { id: `c-att-${Date.now()}`, userId: currentUser.id, timestamp: new Date().toISOString(), text: newComment.trim() };
    try {
      await onUpdateAttendance(attendance.id, { comments: [...(attendance.comments || []), comment] });
      const mentionedIds = extractMentionedUserIds(newComment);
      const uniqueMentions = mentionedIds.filter(id => id !== currentUser.id);
      if (uniqueMentions.length > 0) {
        const notifications = uniqueMentions.map(userId => ({
          user_id: userId, type: 'mention', title: 'Mencionaram você',
          description: `${currentUser.name} mencionou você no atendimento de ${contact?.name || 'um cidadão'}`,
          related_id: attendance.id, read: false
        }));
        await supabase.from('notifications').insert(notifications);
      }
      setNewComment('');
    } catch (err) { console.error(err); }
  };

  // Upload Real para Supabase Storage
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    
    setUploading(true);
    try {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
        const filePath = `${currentUser.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('cabinet-files')
            .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data } = supabase.storage.from('cabinet-files').getPublicUrl(filePath);

        const newAttachment: Attachment = {
            id: `att-${Date.now()}`,
            name: file.name,
            url: data.publicUrl,
            type: file.type.includes('image') ? 'image' : 'file',
            size: file.size,
            timestamp: new Date().toISOString()
        };
        
        setEditedFields((prev: any) => ({ ...prev, attachments: [...(prev.attachments || []), newAttachment] }));
    } catch (error: any) {
        setErrorMsg('Erro ao fazer upload: ' + error.message);
    } finally {
        setUploading(false);
        e.target.value = '';
    }
  };

  const removeAttachment = (id: string) => {
    setEditedFields({ ...editedFields, attachments: (editedFields.attachments || []).filter(a => a.id !== id) });
  };

  // Garante que a lista de visualização também esteja limpa
  const viewAttachments = (attendance.attachments || []).map(parseAttachment);

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-navy-950/60 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div className="bg-white dark:bg-navy-900 w-full max-w-4xl max-h-[90vh] rounded-[28px] shadow-2xl flex flex-col overflow-hidden border border-neutral-200 dark:border-navy-800 relative" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 dark:border-navy-800 flex items-center justify-between shrink-0 bg-white dark:bg-navy-900">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-600 text-white shadow-lg"><ClipboardList size={20} /></div>
            <h2 className="text-lg font-black text-slate-800 dark:text-white tracking-tight">{isEditing ? 'Configurar Atendimento' : 'Detalhes do Atendimento'}</h2>
          </div>
          <div className="flex items-center gap-2">
             {!isEditing && canEdit && <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-5 py-2 bg-neutral-100 dark:bg-navy-800 text-neutral-600 rounded-xl font-black text-[10px] uppercase hover:bg-neutral-200 transition-colors">Editar</button>}
             <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full text-neutral-400"><X size={20} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar px-6 py-6 space-y-6">
          {isEditing ? (
             <div className="space-y-5 animate-in slide-in-from-left-4">
                {errorMsg && <div className="p-3 bg-red-50 text-red-600 rounded-xl text-xs font-bold flex items-center gap-2"><AlertTriangle size={14}/> {errorMsg}</div>}
                <div className="space-y-1.5">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Assunto Principal</label>
                   <input value={editedFields.demand} onChange={e => setEditedFields({...editedFields, demand: e.target.value})} className="w-full text-lg font-black bg-white dark:bg-navy-800 border border-slate-200 rounded-xl px-4 py-3 outline-none dark:text-white" />
                </div>
                <div className="space-y-1.5">
                   <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Resumo Detalhado</label>
                   <textarea value={editedFields.summary} onChange={e => setEditedFields({...editedFields, summary: e.target.value})} rows={3} className="w-full bg-white dark:bg-navy-800 border border-slate-200 rounded-xl p-4 outline-none dark:text-white text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                   <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Solicitante</label>
                      <SearchableSelect options={contacts.map(c => ({ value: c.id, label: c.name }))} value={editedFields.contactid} onChange={val => setEditedFields({...editedFields, contactid: val})} className="w-full bg-white dark:bg-navy-900 border border-neutral-100 rounded-xl px-4 py-3 text-sm font-bold dark:text-white" />
                   </div>
                   <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Responsável</label>
                      <select value={editedFields.responsible_id} onChange={e => setEditedFields({...editedFields, responsible_id: e.target.value})} className="w-full bg-white dark:bg-navy-950 border border-neutral-100 rounded-xl px-4 py-3 text-sm font-bold dark:text-white outline-none">
                        <option value="">Não atribuído</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                   </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                   <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Canal</label>
                      <select value={editedFields.channel} onChange={e => setEditedFields({...editedFields, channel: e.target.value})} className="w-full bg-white dark:bg-navy-900 border border-neutral-100 rounded-xl px-4 py-3 text-sm font-bold dark:text-white">
                        {['WhatsApp', 'Presencial', 'Telefone', 'E-mail', 'Redes Sociais'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                   </div>
                   <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Urgência</label>
                      <select value={editedFields.urgency} onChange={e => setEditedFields({...editedFields, urgency: e.target.value})} className="w-full bg-white dark:bg-navy-900 border border-neutral-100 rounded-xl px-4 py-3 text-sm font-bold dark:text-white outline-none">
                        {['Baixa', 'Média', 'Alta', 'Urgente'].map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                   </div>
                   <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Situação</label>
                      <select value={editedFields.status} onChange={e => setEditedFields({...editedFields, status: e.target.value as AttendanceStatus})} className="w-full bg-white dark:bg-navy-900 border border-neutral-100 rounded-xl px-4 py-3 text-sm font-bold dark:text-white outline-none">
                        {Object.values(AttendanceStatus).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                   </div>
                </div>

                {/* Anexos (Restaurado) */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2"><Paperclip size={14}/> Anexos</label>
                        <label className="cursor-pointer text-[10px] font-bold text-parliament-600 uppercase hover:underline flex items-center gap-1">
                           {uploading ? <Loader2 size={12} className="animate-spin"/> : <Plus size={12}/>} 
                           {uploading ? 'Enviando...' : 'Adicionar Arquivo'}
                           <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} />
                        </label>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {editedFields.attachments && editedFields.attachments.length > 0 ? (
                            editedFields.attachments.map(att => (
                                <div key={att.id} className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-navy-800 border border-slate-200 dark:border-navy-700 rounded-xl shadow-sm">
                                    <File size={14} className="text-slate-400" />
                                    <span className="text-xs font-bold text-slate-700 dark:text-slate-300 truncate max-w-[150px]">{att.name}</span>
                                    <button onClick={() => removeAttachment(att.id)} className="text-slate-400 hover:text-red-500"><X size={12}/></button>
                                </div>
                            ))
                        ) : (
                            <p className="text-xs text-slate-400 italic pl-1">Nenhum anexo.</p>
                        )}
                    </div>
                 </div>

                <div className="flex justify-end gap-3 pt-6">
                   <button onClick={() => { setIsEditing(false); clearDraft(); }} className="px-8 py-3 font-bold text-slate-400">Cancelar</button>
                   <button onClick={handleSave} disabled={saving || uploading} className="bg-emerald-600 text-white px-10 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50">
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salvar Alterações
                   </button>
                </div>
             </div>
          ) : (
            <div className="space-y-6 animate-in fade-in">
                <div className="flex flex-col gap-1.5">
                   <div className="flex items-center gap-2 flex-wrap">
                     <span className={`px-2.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-[0.15em] border w-fit ${attendance.status === 'Concluído' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-amber-50 text-amber-600 border-amber-100'}`}>{attendance.status}</span>
                     <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1"><Clock size={10}/> {attendance.urgency}</span>
                     <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest flex items-center gap-1"><Radio size={10}/> {attendance.channel}</span>
                     {attendance.source === 'public_form' && (
                       <span className="px-2.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-[0.15em] border w-fit bg-emerald-50 text-emerald-600 border-emerald-100 flex items-center gap-1"><Globe size={10}/> Formulário Público</span>
                     )}
                   </div>
                   <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight leading-tight">{attendance.demand}</h1>
                </div>

                <div className="grid grid-cols-2 gap-4">
                   <div className="bg-white dark:bg-navy-800 p-4 rounded-2xl border border-slate-100 dark:border-navy-700 shadow-sm flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-navy-700 text-emerald-600 dark:text-white flex items-center justify-center font-black text-sm flex-shrink-0">{contact?.name.charAt(0) || '?'}</div>
                      <div className="min-w-0"><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Solicitante</p><p className="font-bold text-sm text-navy-950 dark:text-white truncate">{contact?.name || 'Não identificado'}</p></div>
                   </div>
                   <div className="bg-white dark:bg-navy-800 p-4 rounded-2xl border border-slate-100 dark:border-navy-700 shadow-sm flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-parliament-50 dark:bg-navy-700 text-parliament-600 dark:text-white flex items-center justify-center font-black text-sm flex-shrink-0">{responsible?.name.charAt(0) || '?'}</div>
                      <div className="min-w-0"><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Responsável</p><p className="font-bold text-sm text-navy-950 dark:text-white truncate">{responsible?.name || 'Não atribuído'}</p></div>
                   </div>
                </div>

                {/* Anexos (Visualização) */}
                {viewAttachments.length > 0 && (
                    <div className="space-y-3">
                        <h3 className="text-sm font-black text-slate-900 dark:text-white flex items-center gap-2"><Paperclip size={16} className="text-slate-400"/> Anexos</h3>
                        <div className="flex flex-wrap gap-4">
                            {viewAttachments.map((att: any) => (
                                <div key={att.id || Math.random()} className="flex items-center gap-3 p-4 bg-white dark:bg-navy-900 border border-slate-100 dark:border-navy-800 rounded-2xl shadow-sm">
                                    <div className="p-2 bg-slate-50 dark:bg-navy-800 rounded-lg text-slate-400"><File size={20}/></div>
                                    <div>
                                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300 max-w-[200px] truncate" title={att.name}>{att.name || 'Sem nome'}</p>
                                        <p className="text-[10px] text-slate-400">{att.timestamp ? new Date(att.timestamp).toLocaleDateString() : '-'}</p>
                                    </div>
                                    <a href={att.url} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-300 hover:text-parliament-600"><Download size={16}/></a>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="space-y-3">
                   <h3 className="text-sm font-black dark:text-white flex items-center gap-2 border-l-4 border-emerald-600 pl-3">Resumo do Atendimento</h3>
                   <div className="p-5 bg-neutral-50 dark:bg-navy-950 rounded-2xl border border-slate-100 dark:border-navy-800">
                      <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed font-medium">
                         {attendance.summary || "Nenhum resumo detalhado foi registrado ainda."}
                      </p>
                   </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-navy-800">
                  <h3 className="text-sm font-black dark:text-white flex items-center gap-2"><MessageSquare size={16} className="text-emerald-600" /> Notas e Acompanhamento</h3>
                  <div className="space-y-4 mb-6">
                    {attendance.comments && attendance.comments.length > 0 ? attendance.comments.map(c => {
                       const user = users.find(u => u.id === c.userId);
                       return (
                         <div key={c.id} className="p-4 bg-white dark:bg-navy-800 rounded-2xl border border-slate-100 dark:border-navy-700 shadow-sm">
                            <div className="flex justify-between mb-2">
                               <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">{user?.name || 'Sistema'}</span>
                               <span className="text-[9px] font-bold text-slate-400">{new Date(c.timestamp).toLocaleString('pt-BR')}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 leading-relaxed"><FormattedText text={c.text} mentionClassName="text-emerald-600 dark:text-emerald-400 font-bold" /></p>
                         </div>
                       );
                    }) : <p className="text-center py-6 text-slate-400 text-xs italic">Nenhuma nota registrada.</p>}
                  </div>
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 flex items-center justify-center shrink-0"><MessageSquare size={18}/></div>
                    <div className="flex-1 relative">
                      <MentionInput 
                        value={newComment} onChange={setNewComment} users={users} placeholder="Adicionar nota..." className="w-full bg-white dark:bg-navy-900 border border-slate-200 dark:border-navy-800 rounded-3xl px-6 py-4 outline-none focus:ring-4 focus:ring-emerald-50 dark:text-white shadow-inner resize-none text-sm font-medium"
                        onKeyDown={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                      />
                      <button onClick={handleAddComment} disabled={!newComment.trim()} className="absolute right-4 bottom-4 p-2.5 bg-emerald-600 text-white rounded-xl hover:scale-105 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50"><Send size={16} /></button>
                    </div>
                  </div>
                </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceDetailModal;
